<template>
    <div>
        <ul v-if="item" id="campaign_provisioning" class="cic-container">
            <li class="cic-header">
                <span class="cic-header-title">
                    <span>{{ $t('Campaign Provisioning') }}</span>
                    <span v-if="item.scriptId > 0">: {{ item.name }}</span>
                </span>
            </li>
            <li>
                <div class="bodySection">
                    <div style="font: bold 14px Verdana; text-align: left">{{ $t('Service Details') }}</div>
                    <br />
                    <table class="form-table">
                        <tbody>
                            <tr>
                                <td>
                                    <label>{{ $t('Service') }}:</label>
                                </td>
                                <td colspan="2">
                                    <CicSelect
                                        id="campaign_script"
                                        :options="
                                            scripts.map((script) => ({ value: script.scriptId, label: script.name }))
                                        "
                                        :placeholder="$t('Please select...')"
                                        :value="item.scriptId > 0 ? item.scriptId : undefined"
                                        @input="(val) => loadCampaign(val)"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <EditTableViews
                        v-if="item.scriptId > 0"
                        :dynamic-queues="dynamicQueues"
                        :item="item"
                        :multiple-views="multipleViews"
                        :queues="queues"
                        :views="views"
                        @change="updateFieldValue"
                        @changeView="(val) => loadColumns([val])"
                    />
                </div>
                <div v-if="item.scriptId > 0" class="bodySection">
                    <div style="font: bold 14px Verdana; text-align: left">{{ $t('Campaign Details') }}</div>
                    <br />
                    <EditRetries
                        :batch-prioritisation="batchPrioritisation"
                        :item="item"
                        :retry-profiles="retryProfiles"
                        :retry-script="retryScript"
                        :scripts="scripts"
                        @change="updateFieldValue"
                    />
                    <table class="form-table">
                        <tbody>
                            <tr>
                                <td>
                                    <label>{{ $t('Maximum ring time') }}:</label>
                                </td>
                                <td>
                                    <CicSelect
                                        id="campaign_maxringtime"
                                        :options="
                                            [15, 20, 25, 30, 35, 40, 45, 50, 55, 60].map((time) => ({
                                                value: time,
                                                label: $t('[x] seconds').replace(/\[x]/g, time),
                                            }))
                                        "
                                        :value="item.maxRingTime"
                                        @input="(val) => updateFieldValue('maxRingTime', val)"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <EditCli
                        :cli-profiles="cliProfiles"
                        :item="item"
                        :maxlength="maxCLILength"
                        :multiple-views="multipleViews"
                        :views="views"
                        @change="updateFieldValue"
                    />
                    <table class="form-table">
                        <tbody>
                            <tr>
                                <td>
                                    <label>{{ $t('Enable Do Not Process List') }}:</label>
                                </td>
                                <td>
                                    <CicCheckbox
                                        id="campaign_dnp"
                                        :value="item.useDoNotProcess"
                                        @input="(val) => updateFieldValue('useDoNotProcess', val)"
                                    />
                                </td>
                            </tr>
                            <EditDoNotProcess
                                v-if="item.useDoNotProcess"
                                :item="item"
                                :views="views"
                                :view-ids="usedViewIds"
                                :tables="tables"
                                @change="(val) => updateFieldValue('doNotProcess', val)"
                            />
                        </tbody>
                    </table>
                    <EditTimes
                        :item="item"
                        :multiple-views="multipleViews"
                        :times-profiles="timesProfiles"
                        :views="views"
                        @change="updateFieldValue"
                    />
                    <table class="form-table">
                        <tbody>
                            <template v-if="$store.hasRight('DIAL_RIGHTS_SHOW_MAX_SIMULTANEOUS_CALLS_OPTION')">
                                <tr>
                                    <td>
                                        <label>{{ $t('Unlimited maximum simultaneous calls') }}:</label>
                                    </td>
                                    <td>
                                        <CicCheckbox
                                            id="campaign_nocalllimit"
                                            :value="item.maxSimultaneousCalls === null"
                                            @input="(val) => updateFieldValue('maxSimultaneousCalls', val ? null : '')"
                                        />
                                    </td>
                                </tr>
                                <tr v-if="item.maxSimultaneousCalls !== null">
                                    <td class="sub-option">
                                        {{ $t('Limit') }}
                                    </td>
                                    <td>
                                        <CicTextbox
                                            id="campaign_maxcalls"
                                            :value="item.maxSimultaneousCalls"
                                            int-only
                                            @input="(val) => updateFieldValue('maxSimultaneousCalls', val)"
                                        />
                                    </td>
                                </tr>
                            </template>
                            <tr v-if="$store.hasRight('DIAL_RIGHTS_SHOW_TPS_CHECK_OPTION')">
                                <td>
                                    <label>{{ $t('Check Telephone Preference Service (TPS) list') }}:</label>
                                </td>
                                <td>
                                    <CicCheckbox id="campaign_tps" :value="item.checkTPSList" @input="updateTPS" />
                                </td>
                            </tr>
                            <tr>
                                <td>
                                    <label>{{ $t('Enable select Recorder user') }}:</label>
                                </td>
                                <td>
                                    <CicCheckbox
                                        id="campaign_recorder"
                                        :value="item.useRecorder"
                                        @input="(val) => updateFieldValue('useRecorder', val)"
                                    />
                                </td>
                            </tr>
                            <tr v-if="item.useRecorder">
                                <td class="sub-option">{{ $t('Select Recorder user') }}:</td>
                                <td>
                                    <CicSelect
                                        id="campaign_recorderusers"
                                        :options="users.map((user) => ({ value: user.userId, label: user.username }))"
                                        :value="item.recorderUserIds"
                                        multiple
                                        @input="(val) => updateFieldValue('recorderUserIds', val)"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div v-if="item.scriptId > 0" class="bodySection">
                    <div style="font: bold 14px Verdana; text-align: left">{{ $t('Campaign Dates') }}</div>
                    <br />
                    <EditTimeOfDaySchedule :item="item" @change="updateFieldValue" />
                    <br />
                    <table id="scriptSchedule" style="margin-left: 0; text-align: center">
                        <thead>
                            <tr>
                                <th>{{ $t('Start Date') }}</th>
                                <th>{{ $t('End Date') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style="padding: 2px">
                                    <CicDatePicker
                                        :enabled="!item.window.stopped"
                                        :value="item.window.start"
                                        format="YYYY-MM-DD HH:mm"
                                        type="datetime"
                                        @input="(val) => updateWindow('start', val)"
                                    />
                                </td>
                                <td style="padding: 2px">
                                    <CicDatePicker
                                        :enabled="!item.window.stopped"
                                        :value="item.window.end"
                                        format="YYYY-MM-DD HH:mm"
                                        type="datetime"
                                        @input="(val) => updateWindow('end', val)"
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </li>
            <li class="cic-footer">
                <CicButton id="campaign_cancel" @click="handleClick">
                    {{ $t('Cancel') }}
                </CicButton>
                <CicButton v-if="item.scriptId > 0" id="campaign_save" @click="saveItem">
                    {{ $t('Save') }}
                </CicButton>
            </li>
        </ul>
    </div>
</template>

<script>
    import moment from 'moment';
    import CicButton from '@/components/CicButton';
    import CicDatePicker from '@/components/CicDatePicker';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicSelect from '@/components/inputs/CicSelect';
    import CicTextbox from '@/components/inputs/CicTextbox';
    import { load, save } from '@/utils/Http';
    import EditCli from './EditCli';
    import EditDoNotProcess from './EditDoNotProcess';
    import EditRetries from './EditRetries';
    import EditTableViews from './EditTableViews';
    import EditTimeOfDaySchedule from './EditTimeOfDaySchedule';
    import EditTimes from './EditTimes';
    import './resources/styles/campaigns.scss';
    import { pick } from 'lodash';

    export default {
        name: 'EditCampaign',
        components: {
            CicButton,
            CicCheckbox,
            CicDatePicker,
            CicSelect,
            CicTextbox,
            EditCli,
            EditDoNotProcess,
            EditRetries,
            EditTableViews,
            EditTimeOfDaySchedule,
            EditTimes,
        },
        props: {
            scriptId: {
                type: Number,
                default: -1,
            },
        },
        data() {
            return {
                batchPrioritisation: false,
                cliBlacklist: [],
                cliProfiles: [],
                dynamicQueues: false,
                item: null,
                maxCLILength: 0,
                minCLILength: 0,
                multipleViews: false,
                queues: [],
                retryProfiles: [],
                retryScript: false,
                scripts: [],
                tables: [],
                timesProfiles: [],
                users: [],
                views: [],
            };
        },
        computed: {
            usedViewIds() {
                const ids = this.item.useMultipleViews
                    ? this.item.viewRules
                          .map(({ views }) => views)
                          .flat()
                          .map(({ viewId }) => parseInt(viewId))
                    : [parseInt(this.item.viewId)];

                return ids.filter((id) => id > 0);
            },
        },
        created() {
            this.loadSelectData(this.scriptId);
        },
        methods: {
            loadCampaign(scriptId) {
                this.item = null;
                load('campaign/get', { scriptId }, (response) => {
                    this.batchPrioritisation = response.batchPrioritisation;
                    this.cliBlacklist = response.cliBlacklist;
                    this.dynamicQueues = response.dynamicQueues;
                    this.item = response.campaign;
                    this.item.schedules = response.timeBands;
                    this.multipleViews = response.multipleViews;
                    this.queues = response.queues;
                    this.retryScript = response.retryScript;

                    if (!this.item.window) {
                        this.item.window = {
                            start: moment(moment().format('YYYY-MM-DD 00:00:00')),
                            end: moment(moment().format('YYYY-MM-DD 23:59:00')),
                            stopped: false,
                        };
                    } else {
                        this.item.window.start = moment(this.item.window.start);
                        this.item.window.end = moment(this.item.window.end);
                    }

                    const viewIds = [];
                    if (this.item.viewId > 0) {
                        viewIds.push(this.item.viewId);
                    }
                    if (this.item.useMultipleViews) {
                        this.item.viewRules.forEach((rule) => {
                            rule.views.forEach((view) => {
                                if (view.viewId > 0 && !viewIds.includes(view.viewId)) {
                                    viewIds.push(view.viewId);
                                }
                            });
                        });
                    }
                    if (viewIds.length > 0) {
                        this.loadColumns(viewIds);
                    }
                });
            },
            loadColumns(viewIds) {
                load('campaign/getcolumns', { viewIds }, (response) => {
                    response.views.forEach((view) => {
                        const tableView = this.views.find((v) => v.viewId === view.viewId);
                        if (tableView) {
                            tableView.columns = view.columns;
                        }
                    });
                });
            },
            loadSelectData(scriptId) {
                load('campaign/getdata', {}, (response) => {
                    this.cliProfiles = response.cliProfiles;
                    this.maxCLILength = response.maxCLILength;
                    this.minCLILength = response.minCLILength;
                    this.retryProfiles = response.retryProfiles;
                    this.scripts = response.scripts;
                    this.timesProfiles = response.timesProfiles;
                    this.users = response.users;
                    this.views = response.views;
                    this.tables = response.tables;
                    if (scriptId > 0) {
                        this.loadCampaign(scriptId);
                    } else {
                        this.item = { scriptId };
                    }
                });
            },
            saveItem() {
                const required = [
                    { required: !this.item.useMultipleViews && !this.item.viewId, label: 'Table View' },
                    { required: this.item.useRetries && !this.item.retryProfileId, label: 'Retry Profile' },
                    {
                        required:
                            this.item.useRetries &&
                            this.retryScript &&
                            this.item.useRetryScript &&
                            !this.item.retryScriptId,
                        label: 'Service',
                    },
                    {
                        required:
                            this.item.useRetries &&
                            this.item.prioritisation === 2 &&
                            this.item.unprocessedPercentage === '',
                        label: 'Unprocessed Records',
                    },
                    {
                        required:
                            this.item.useRetries && this.item.prioritisation === 2 && this.item.retryPercentage === '',
                        label: 'Retries',
                    },
                    {
                        required:
                            this.$store.hasRight('ACCESS_RIGHTS_ENABLE_PRESENTATION_CLI') &&
                            (!this.item.presentationCLI || this.item.presentationCLI.trim() === ''),
                        label: this.item.useCLIProfile ? 'Default address' : 'Address',
                    },
                    {
                        required: !this.item.useMultipleViews && this.item.useCLIProfile && !this.item.cliColumn,
                        label: 'Table View column',
                    },
                    {
                        required: !this.item.useMultipleViews && this.item.useCLIProfile && !this.item.cliProfileId,
                        label: 'CLI Profile',
                    },
                    {
                        required: !this.item.useMultipleViews && this.item.useTimesProfile && !this.item.timesColumn,
                        label: 'Table View column',
                    },
                    {
                        required: !this.item.useMultipleViews && this.item.useTimesProfile && !this.item.timesProfileId,
                        label: 'Dial Times Profile',
                    },
                    {
                        required:
                            this.$store.hasRight('DIAL_RIGHTS_SHOW_MAX_SIMULTANEOUS_CALLS_OPTION') &&
                            this.item.maxSimultaneousCalls !== null &&
                            this.item.maxSimultaneousCalls === '',
                        label: 'Limit',
                    },
                    {
                        required: !this.item.window.start,
                        label: 'Start Date',
                    },
                    {
                        required: !this.item.window.end,
                        label: 'End Date',
                    },
                    {
                        required:
                            this.item.useSchedules && this.item.schedules.some((schedule) => schedule.start === ''),
                        label: 'Start',
                    },
                    {
                        required: this.item.useSchedules && this.item.schedules.some((schedule) => schedule.end === ''),
                        label: 'End',
                    },
                ];

                const firstRequired = required.find((require) => require.required);
                if (firstRequired) {
                    alert(this.$t('REQUIRE_PARAMETER').replace(/\[x]/g, this.$t(firstRequired.label)));
                    return;
                }

                if (this.item.presentationCLI && this.item.presentationCLI.trim() !== '') {
                    if (
                        this.cliBlacklist.some(
                            (prefix) => this.item.presentationCLI.substring(0, prefix.length) === prefix,
                        )
                    ) {
                        alert(this.$t('RR_CLI_BARRED'));
                        return;
                    }
                    // Check CLI is at least than minimum length
                    if (this.minCLILength > 0 && this.item.presentationCLI.length < this.minCLILength) {
                        alert(this.$t('RR_CLI_TOOSHORT').replace('[x]', this.minCLILength));
                        return;
                    }
                }

                const params = {
                    ...this.item,
                    useRetries: this.item.useRetries ? 1 : 0,
                    useRetryScript: this.item.useRetryScript ? 1 : 0,
                    useMultipleViews: this.item.useMultipleViews ? 1 : 0,
                    useCLIProfile: this.item.useCLIProfile ? 1 : 0,
                    useTimesProfile: this.item.useTimesProfile ? 1 : 0,
                    useSchedules: this.item.useSchedules ? 1 : 0,
                    useRecorder: this.item.useRecorder ? 1 : 0,
                    useDoNotProcess: this.item.useDoNotProcess ? 1 : 0,
                    checkTPSList: this.item.checkTPSList ? 1 : 0,
                    useDynamicQueues: this.item.useDynamicQueues ? 1 : 0,
                    schedules: JSON.stringify(this.item.schedules),
                    viewRules: JSON.stringify(this.item.viewRules),
                    recorderUserIds: JSON.stringify(this.item.recorderUserIds),
                    doNotProcess: JSON.stringify(this.item.doNotProcess),
                    window: null,
                    windowStart: this.item.window.start.format('YYYY-MM-DD HH:mm:ss'),
                    windowEnd: this.item.window.end.format('YYYY-MM-DD HH:mm:ss'),
                };
                save('campaign/update', params, () => {
                    this.loadCampaign(this.item.scriptId);
                });
            },
            updateFieldValue(name, value) {
                const item = { ...this.item };
                item[name] = value;
                if (name === 'viewId') {
                    item.cliColumn = null;
                    item.timesColumn = null;
                    delete item.doNotProcess[this.item.viewId];
                }

                if (name === 'viewRules') {
                    const viewIds = value
                        .map(({ views }) => views)
                        .flat()
                        .map(({ viewId }) => viewId);

                    item.doNotProcess = pick(item.doNotProcess, viewIds);
                }

                if (name === 'useDoNotProcess') {
                    item.doNotProcess = {};
                }

                this.item = item;
            },
            updateTPS(value) {
                if (value || confirm(this.$t('RR_CONFIRM_NO_TPS'))) {
                    this.updateFieldValue('checkTPSList', value);
                }
            },
            updateWindow(name, value) {
                const window = { ...this.item.window };
                window[name] = value;
                this.updateFieldValue('window', window);
            },
            handleClick() {
                this.$router.push('/menu');
                this.$store.setMenuOption(null);
            },
        },
    };
</script>
